import { Box, IconButton, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../../../../api/hooks/useSmartAndRegularAssets";
import { labelKeys } from "../../../../../../api/queryKeys";
import CustomToolTip from "../../../../../../components/CustomTooltip";
import { resetFiltersToDefaults } from "../../../../../../components/filters/ClearFiltersButton";
import SvgToIcon from "../../../../../../icons/SvgToIcon";
import CustomMenu from "../../../../../settings/inbox/MoreInfoMenu";
import { hasAnyFiltersApplied } from "../../../../../stack/environment/filters/utils";
import {
  icons,
  iconsAsset,
} from "../../../../../stack/environment/icons/iconExport";

export default function FilterMisconfigurationsButton({
  appliedFilters,
  setAppliedFilters,
  disabled = false,
  ...props
}) {
  const colors = useColors();
  const { getAllLabels, trackEvent } = useAPI();
  const [anchorEl, setAnchorEl] = useState(null);

  const { regularAndActiveSmartAsset: assets } =
    useSmartAndRegularAssets(false);

  const { data: labelsData } = useQuery({
    queryKey: labelKeys.all,
    queryFn: getAllLabels,
    select: (data) => data.data,
  });

  const labels = labelsData?.filter((label) => !label.is_deleted) || [];

  const handleClick = (event) => {
    trackEvent && trackEvent("User clicked on filter by labels button");
    setAnchorEl(event.currentTarget);
  };

  const moreActions = [
    {
      title: "Critical Assets",
      key: "assets",
      submenuTitle: "Select assets",
      trailingIcon: icons.chevronRightIcon,
      submenuMultiple: true,
      submenu: assets?.map((option) => ({
        title: (
          <Box
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
            gap={1}
          >
            <SvgToIcon
              IconLogo={iconsAsset[option.type]}
              style={{ width: "16px" }}
            />
            <Typography variant="caption">{option.name}</Typography>
          </Box>
        ),
        key: option.asset_id,
        selected: appliedFilters.assets[option.asset_id] === true,
        onclick: () => {
          trackEvent &&
            trackEvent("User filtered by assets option: " + option.name);
          setAppliedFilters({
            ...appliedFilters,
            assets: {
              ...appliedFilters.assets,
              [option.asset_id]: !appliedFilters.assets[option.asset_id],
            },
          });
        },
        disabled: !assets || assets.length === 0,
      })),
      icon: icons.criticalAssetIcon,
    },
    {
      title: "Labels",
      key: "labels",
      submenuTitle: "Select labels",
      disabled: false,
      trailingIcon: icons.chevronRightIcon,
      submenuMultiple: true,
      submenu: labels?.map((option) => ({
        title: (
          <Box
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
            gap={1}
          >
            <SvgToIcon IconLogo={icons.LabelIcon} style={{ width: "18px" }} />
            <Typography variant="caption">{option.name}</Typography>
          </Box>
        ),
        key: option.label_id,
        selected: appliedFilters.labels[option.label_id] === true,
        onclick: () => {
          trackEvent("The user filtered by labels option: " + option.name);
          setAppliedFilters({
            ...appliedFilters,
            labels: {
              ...appliedFilters.labels,
              [option.label_id]: !appliedFilters.labels[option.label_id],
            },
          });
        },
        disabled: !labels || labels.length === 0,
      })),
      icon: icons.LabelOutlineIcon,
    },
    {
      title: "Clear filters",
      key: "clearFilters",
      type: "button",
      onclick: () => {
        resetFiltersToDefaults(appliedFilters, setAppliedFilters);
        trackEvent && trackEvent("User cleared filters");
      },
      disabled:
        !hasAnyFiltersApplied(appliedFilters.labels) &&
        !hasAnyFiltersApplied(appliedFilters.assets),
    },
  ];
  return (
    <Box>
      <CustomToolTip title={disabled ? "" : "Filter by Labels"} placement="top">
        <IconButton
          sx={{
            background: colors.common_white,
            color: colors.action_active,
            height: "32px",
            width: "32px",
            border: "1px solid " + colors.enabled_stroke,
            borderRadius: "8px",
            textTransform: "none",
            whiteSpace: "nowrap",
            "&:hover": {
              background: colors.grey_8percent,
            },
            ...props.sx,
          }}
          onClick={handleClick}
          disabled={disabled}
        >
          <SvgToIcon
            IconLogo={icons.FilterIcon}
            style={{
              width: "18px",
              color: disabled
                ? colors.action_disabled
                : colors.icon_action_active,
            }}
          />
        </IconButton>{" "}
      </CustomToolTip>
      <CustomMenu
        title="Filter by..."
        anchorElMenu={anchorEl}
        openMenu={Boolean(anchorEl)}
        handleCloseMenu={() => setAnchorEl(null)}
        moreActions={moreActions}
      />
    </Box>
  );
}
